import { useCallback } from 'react'
import type { ShapeTextRangeEdit } from '@/lib/shapes/types/shape-text-range-edit'
import { planShapeTextReplacements } from '../helpers/plan-shape-text-replacements'
import { getShapeById } from '../helpers/shape-list'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { ShapeTextCommitApi } from './use-shape-text-commit'
import type { ShapeUpdatesApi } from './use-shape-updates'

export function useShapeTextReplace(
  ctx: ShapeManagerStateApi & ShapeUpdatesApi & ShapeTextCommitApi
) {
  const {
    flushPendingShapeTextCommit,
    onShapeCommitted,
    resyncShapeTextEditBaseline,
    shapesRef,
    updateShape,
  } = ctx

  /**
   * Rewrites ranges of shapes' text as one undo step (Spell Check's Change and Change All). Returns,
   * per replacement, whether it was written; see `planShapeTextReplacements` for when one is not.
   *
   * Committed last shape first, like a cross-frame text edit (`mutateChainTextSelection`), so a
   * chain's head reflows after its members are already rewritten.
   */
  const replaceShapeTextRanges = useCallback(
    (replacements: readonly (readonly ShapeTextRangeEdit[])[]): boolean[] => {
      // The ranges were measured on the live text, so a keystroke still waiting to commit lands
      // first, or the committed paragraphs would lag what was measured.
      flushPendingShapeTextCommit()

      const { written, patches } = planShapeTextReplacements(shapesRef.current, replacements)
      const historyTxnId = `shape-text-replace:${crypto.randomUUID()}`

      for (const patch of [...patches].reverse()) {
        // Freeze is placement-only, so it must not block a text rewrite; locked frames were
        // already refused by the plan.
        updateShape(patch.shapeId, patch.after, { bypassEditGuards: true })

        const latest = getShapeById(shapesRef.current, patch.shapeId)

        if (latest) {
          onShapeCommitted?.(latest, 'update', {
            before: patch.before,
            after: patch.after,
            historyTxnId,
          })
        }

        // A frame open for editing must diff its next commit against the rewritten text.
        resyncShapeTextEditBaseline(patch.shapeId)
      }

      return written
    },
    [
      flushPendingShapeTextCommit,
      onShapeCommitted,
      resyncShapeTextEditBaseline,
      shapesRef,
      updateShape,
    ]
  )

  return { replaceShapeTextRanges }
}

export type ShapeTextReplaceApi = ReturnType<typeof useShapeTextReplace>
